/**
 * Requirement picker (section-editor side, spec §4.4) — the inverse of the
 * analysis-side section picker. Lists the bid's narrative compliance items +
 * leaf evaluation criteria, unmapped first, weight shown on criteria. Both
 * sides write the same links table.
 */

import { useMemo, useState, type ReactNode } from 'react'
import { ClipboardCheck, Scale } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { useCoverageStore } from '@/stores/coverage-store'
import {
  useRequirementDirectory,
  type RequirementEntry,
} from '@/components/requirement-coverage/useRequirementDirectory'

interface RequirementPickerPopoverProps {
  bidId: string
  /** Requirement ids already linked to this section — hidden from the list. */
  linkedIds?: Set<string>
  onSelect: (requirement: RequirementEntry) => void
  children: ReactNode
  /** Keeps the popover inside this element (e.g. a dialog) rather than the viewport. */
  collisionBoundary?: Element
}

export function RequirementPickerPopover({
  bidId,
  linkedIds,
  onSelect,
  children,
  collisionBoundary,
}: RequirementPickerPopoverProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const { entries: directory } = useRequirementDirectory(open ? bidId : null)
  const coverage = useCoverageStore((s) => s.coverage)

  const statusById = useMemo(() => {
    const map = new Map<string, string>()
    for (const row of [...(coverage?.compliance ?? []), ...(coverage?.criteria ?? [])]) {
      map.set(row.requirementId, row.status)
    }
    return map
  }, [coverage])

  const options = useMemo(() => {
    if (!directory) return null
    const q = search.trim().toLowerCase()
    return directory
      .filter(
        (entry) =>
          entry.isLeaf &&
          (entry.kind === 'criterion' || entry.responseExpectation === 'narrative') &&
          !linkedIds?.has(entry.id) &&
          (!q || entry.title.toLowerCase().includes(q))
      )
      .sort((a, b) => {
        // Unmapped first, then criteria by weight descending
        const aUnmapped = statusById.get(a.id) === 'unmapped' ? 0 : 1
        const bUnmapped = statusById.get(b.id) === 'unmapped' ? 0 : 1
        if (aUnmapped !== bUnmapped) return aUnmapped - bUnmapped
        return (b.weightPercentage ?? 0) - (a.weightPercentage ?? 0)
      })
  }, [directory, search, linkedIds, statusById])

  const pick = (entry: RequirementEntry) => {
    setOpen(false)
    setSearch('')
    onSelect(entry)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className="w-80 p-0"
        align="start"
        {...(collisionBoundary ? { collisionBoundary } : {})}
        collisionPadding={12}
      >
        <div className="border-b border-border p-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search requirements…"
            className="h-8 text-xs"
            autoFocus
          />
        </div>
        <div className="max-h-72 overflow-y-auto p-1">
          {options === null ? (
            <p className="px-2 py-3 text-meta">Loading requirements…</p>
          ) : options.length === 0 ? (
            <p className="px-2 py-3 text-meta">No requirements to map.</p>
          ) : (
            options.map((entry) => (
              <button
                key={entry.id}
                type="button"
                onClick={() => pick(entry)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-accent"
              >
                {entry.kind === 'compliance' ? (
                  <ClipboardCheck className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                ) : (
                  <Scale className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                )}
                <span className="min-w-0 flex-1 truncate text-xs">{entry.title}</span>
                {entry.weightPercentage != null && entry.weightPercentage > 0 && (
                  <span className="shrink-0 text-[11px] font-medium text-muted-foreground">
                    {entry.weightPercentage}%
                  </span>
                )}
                {statusById.get(entry.id) === 'unmapped' && (
                  <span className="shrink-0 text-[11px] text-[hsl(var(--status-warning))]">
                    unmapped
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
